<script setup>
import { defineProps, defineEmits } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMenu, ElMenuItem, ElIcon } from "element-plus";
import {
  HomeIcon,
  BellIcon,
  SpeakerphoneIcon,
  UsersIcon,
  ChatAltIcon,
  FolderIcon,
  VideoCameraIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from "@heroicons/vue/outline";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { sidebarMenu, adminPortalMenu } from "../data/dummyLayoutData";
import { useAuthStore } from "@/stores";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import ReportsMenu from "./ReportsMenu.vue";
import AdminPortalMenu from "./AdminPortalMenu.vue";
import UserDisplay from "./UserDisplay.vue";

defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["navigate"]);

const layoutStore = useLayoutStore();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const { user } = storeToRefs(authStore);

const iconMap = {
  HomeIcon,
  BellIcon,
  SpeakerphoneIcon,
  UsersIcon,
  ChatAltIcon,
  FolderIcon,
  VideoCameraIcon,
  CalendarIcon,
};

const logo = computed(
  () =>
    user.value?.dealer?.company_layout?.company_logo ||
    "https://lms-v3.s3.us-east-1.amazonaws.com/logo_default_brandonstevens.png"
);
const favicon = "https://lms-v3.s3.us-east-1.amazonaws.com/logo_default_brandonstevens.png";

const activeMenuKey = computed(() => {
  const match = sidebarMenu.find((item) => item.route === route.name);
  return match ? match.key : undefined;
});

const handleSelect = (item) => {
  layoutStore.setActiveMenu(item.key);
  emit("navigate");

  if (item.route) {
    router.push({ name: item.route });
  }
};
</script>
<template>
  <div class="flex flex-col h-full">
    <div
      class="flex items-center h-20 shrink-0 px-4"
      :class="collapsed ? 'justify-center' : 'justify-start gap-2'"
    >
      <img
        :src="collapsed ? favicon : logo"
        class="w-auto mx-auto"
        :class="{
          'h-20': !collapsed,
          'h-6': collapsed,
        }"
      />
    </div>

    <UserDisplay :collapsed="collapsed" show-border class="block xl:hidden" />

    <el-menu
      :collapse="collapsed"
      :ellipsis="false"
      :default-active="activeMenuKey"
      :default-openeds="[adminPortalMenu.key]"
      class="flex-1 overflow-y-auto border-none py-2 compact-menu"
    >
      <el-menu-item
        v-for="item in sidebarMenu"
        :key="item.key"
        :index="item.key"
        @click="handleSelect(item)"
      >
        <el-icon>
          <component :is="iconMap[item.icon]" class="w-5 h-5" />
        </el-icon>
        <template #title>{{ item.label }}</template>
      </el-menu-item>

      <ReportsMenu @navigate="emit('navigate')" />
      <AdminPortalMenu @navigate="emit('navigate')" />
    </el-menu>

    <button
      type="button"
      class="hidden lg:flex items-center justify-center h-11 shrink-0 border-t border-untitled-gray-200 text-untitled-gray-500 hover:bg-untitled-gray-50 hover:text-emerald-700 transition-colors"
      :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="layoutStore.toggleSidebar"
    >
      <ChevronRightIcon v-if="collapsed" class="w-4 h-4" />
      <ChevronLeftIcon v-else class="w-4 h-4" />
    </button>
  </div>
</template>

<style scoped>
.compact-menu :deep(.el-menu-item),
.compact-menu :deep(.el-sub-menu__title) {
  height: 36px;
  line-height: 36px;
  margin: 1px 0;
}

.compact-menu :deep(.el-menu-item .el-icon),
.compact-menu :deep(.el-sub-menu__title .el-icon) {
  margin-right: 8px;
}

.compact-menu :deep(.el-sub-menu .el-menu-item) {
  height: 32px;
  line-height: 32px;
  min-width: unset;
}

.compact-menu :deep(.el-divider--horizontal) {
  margin: 8px 16px;
  width: auto;
}

:deep(.el-menu-item.is-active) {
  background-color: #119381;
  color: #f7f7f7;
  border-right: 3px solid rgb(4 120 87);
}

:deep(.el-menu-item.is-active .el-icon) {
  color: #f7f7f7;
}

:deep(.el-menu-item:hover),
:deep(.el-sub-menu__title:hover) {
  background-color: #119381;
  color: #f7f7f7;
}
</style>
